import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import ImageInput from 'components/field/ImageInput';
import PhoneNumber from 'components/field/PhoneNumber';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import ModuleToolTipContainer from 'components/global/ModuleToolTipContainer';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAppTracking from 'hooks/useAppTracking';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';
import useRestriction, { SYSTEM_LIMITS } from 'hooks/useRestriction';

import BranchFormshort from 'pages/Branches/BranchFormShort';
import BrandForm from 'pages/Settings/Brands/BrandForm';
import WarehouseFormShort from 'pages/Warehouses/WarehouseFormShort';

import { AUTOCOMPLETE, USERS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';
import PATHS from 'utils/paths';

import { faEye } from '@fortawesome/pro-solid-svg-icons';

import ResetPasswordForm from './ResetPasswordForm';

/**
 * @author @omerrkosarrkosar
 *
 */

const UserForm = NiceModal.create(({ id, modalName, mode, setUser, userType: userTypeProp }) => {
	const [isMinimizing, setIsMinimizing] = useState(false);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					userType: yup.string().nullable().required(),
					fullName: yup.string().nullable().min(3).trim().required(),
					jobTitle: yup.string().nullable(),
					email: yup
						.string()
						.nullable()
						.when('$id', ([id], schema) => (id >= 0 ? schema : schema.email().required())),
					phone: yup.string().nullable().phoneNumber(),
					brandIDs: yup.array().nullable(),
					warehouseIDs: yup
						.array()
						.nullable()
						.when(['userType', 'branchIDs'], {
							is: (userType, branchIDs) =>
								(userType === 'warehouseUser' || userType === 'driverUser') &&
								(branchIDs?.length === 0 || !branchIDs),
							then: (schema) => schema.required()
						}),
					branchIDs: yup
						.array()
						.nullable()
						.when('userType', {
							is: (userType) => userType === 'branchUser',
							then: (schema) => schema.required()
						}),
					avatarPath: yup.string().nullable(),
					userCode: yup.string().nullable()
				})
				.required(),
		[]
	);
	const modal = useModal();
	const [isLoading, setIsLoading] = useState(false);

	// View mode for inputs
	const viewMode = mode === 'view';

	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const dispatch = useDispatch();
	const isRestricted = useRestriction();
	const { track } = useAppTracking();
	const { closeModal } = useModalRouter();
	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			fullName: null,
			email: null,
			phone: null,
			userType: userTypeProp ?? null,
			brandIDs: null,
			warehouseIDs: null,
			branchIDs: null,
			jobTitle: null,
			avatarPath: null,
			userCode: null
		},
		context: { id: id ?? -1 }
	});

	const {
		handleSubmit,
		setValue,
		watch,
		setError,
		reset,
		formState: { isDirty, isSubmitSuccessful, isSubmitting }
	} = formMethods;

	// usePrompt(Object.keys(dirtyFields).length && !isSubmitSuccessful);
	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting && !isMinimizing);

	// If mode is edit or view we gotta get the user data .
	const fillForm = useCallback(() => {
		setIsLoading(true);
		axiosPrivate
			.post(USERS.GET_USER, { ID: id })
			.then((response) => {
				const { data, status } = response;

				if (status === 200) {
					reset({
						userType: data.type ?? null,
						fullName: data.fullName ?? null,
						jobTitle: data.jobTitle ?? null,
						email: data.email ?? null,
						phone: data.phone ?? null,
						brandIDs: data.brandIDs ?? null,
						warehouseIDs: data.warehouseIDs ?? null,
						branchIDs: data.branchIDs ?? null,
						avatarPath: data.avatarPath ?? null,
						userCode: data.userCode ?? null
					});
				} else {
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [id]);

	// If the ID came from list page, get the user details
	useEffect(() => {
		if (id) fillForm();
	}, [id]);

	const onSubmit = (data) => {
		setIsLoading(true);

		// reformat url according to state
		const url = id ? USERS.UPDATE_USER : USERS.ADD_USER;
		axiosPrivate
			.post(url, {
				ID: id,
				userType: data.userType,
				brandIDs: data.brandIDs,
				warehouseIDs: data.warehouseIDs,
				branchIDs: data.branchIDs,
				fullName: data.fullName,
				jobTitle: data.jobTitle,
				email: data.email,
				phone: data.phone,
				avatarPath: data.avatarPath,
				userCode: data.userCode
			})
			.then((response) => {
				const { status } = response;

				if (status === 200) {
					setUser && setUser(response.data.ID);
					dispatch(updateDynamicListData(DYNAMICLISTS.USERS));
					showSuccess();
					!id &&
						NiceModal.show(ResetPasswordForm, {
							resetLink: response.data.link,
							email: data.email
						});
					handleCloseModal();
				}
			})
			.catch((error) => {
				if (error.response?.data?.code === 'error.F010') {
					showError({
						header: 'emailAlreadyExists',
						message: 'error.F010'
					});
					setError('email', {
						type: 'manual',
						message: 'error.F010'
					});
				} else {
					showError({
						message: error?.response?.data?.code || error?.response?.data?.message
					});
				}

				AppLogger.error(error);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const brandNameAutocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);

	const defaultColSpan = {
		md: 6
	};
	const isAuthorized = useAccessControl();

	const [userTypes, setUserTypes] = useState(['companyAdmin']);

	useEffect(() => {
		const authorizedUserTypes = [...userTypes];
		if (isAuthorized('MultiWarehouseModule')) authorizedUserTypes.push('warehouseUser');
		if (isAuthorized('BranchModule')) authorizedUserTypes.push('branchUser');
		if (isAuthorized('OTOFlexModule')) authorizedUserTypes.push('driverUser');
		if (isAuthorized('CustomerServiceModule')) authorizedUserTypes.push('customerServiceUser');
		if (isAuthorized('BrandingModule')) authorizedUserTypes.push('BrandManager');

		setUserTypes(authorizedUserTypes);
	}, []);

	const navigate = useNavigate();

	return (
		<Modal
			fullwidth
			name={modalName}
			maxWidth="medium"
			className="modal-userForm"
			minimizeButton={(show, close) => (
				<MinimizeButton
					onShow={() => {
						show();
						setIsMinimizing(false);
					}}
					onClose={close}
					label={mode === 'view' ? 'viewUser' : mode === 'edit' ? 'editUser' : 'addUser'}
					tooltip={['edit', 'view'].includes(mode) ? watch('fullName') : ''}
				/>
			)}
			onMinimize={() => {
				setIsMinimizing(true);
			}}>
			<ModalTitle>
				<Typography variant="p1">
					{mode === 'view' ? 'viewUser' : mode === 'edit' ? 'editUser' : 'addUser'}
				</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods} viewPrintButton={viewMode} borderless>
					<FormItem colSpan={defaultColSpan} label="fullName" isLoading={isLoading} required>
						<StringInput disabled={viewMode} name="fullName" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="mobileNumber" isLoading={isLoading}>
						<PhoneNumber
							onChange={(value) => {
								setValue('phone', value, { shouldValidate: true, shouldDirty: true });
							}}
							disabled={viewMode}
							value={watch('phone')}
							name="phone"
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="email" isLoading={isLoading} required>
						<StringInput disabled={id} name="email" />
					</FormItem>
					<FormItem
						colSpan={defaultColSpan}
						label="userType"
						isLoading={isLoading}
						required
						toolTip={watch('userType') === 'driverUser' ? 'assingToWarehouseOrBranch' : ''}>
						<EnumSelect
							disabled={userTypeProp || viewMode}
							data={userTypes}
							placeholder="selectOne"
							name="userType"
							setValue={(value) => {
								setValue('userType', value, {
									shouldValidate: true,
									shouldDirty: true
								});
								setValue('brandIDs', null, {
									shouldValidate: true,
									shouldDirty: true
								});
								setValue('warehouseIDs', null, {
									shouldValidate: true,
									shouldDirty: true
								});
								setValue('branchIDs', null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							value={watch('userType')}
						/>
					</FormItem>

					{!setUser && (
						<FormItem colSpan={defaultColSpan} label="jobTitle" isLoading={isLoading}>
							<StringInput disabled={viewMode} name="jobTitle" />
						</FormItem>
					)}
					{!setUser && (
						<FormItem colSpan={defaultColSpan} label="userPhoto" isLoading={isLoading}>
							<ImageInput
								value={watch('avatarPath')}
								setValue={(value) => {
									setValue('avatarPath', value, {
										shouldValidate: true,
										shouldDirty: true
									});
								}}
								placeholder="selectImage"
								name="avatarPath"
								disabled={viewMode}
								viewMode={viewMode}
								acceptedFileType="image/*"
							/>
						</FormItem>
					)}
					<FormItem colSpan={defaultColSpan} label="userCode" isLoading={isLoading}>
						<StringInput disabled={viewMode} name="userCode" />
					</FormItem>
					{!setUser && watch('userType') === 'BrandManager' && (
						<FormItem label="assignedBrands" isLoading={isLoading} roleName="/autocomplete/brand">
							<AutoComplete
								disabled={viewMode}
								autoCompleteName={AUTOCOMPLETE.BRANDS}
								setValue={(value) => {
									setValue('brandIDs', value?.map((item) => item.ID) ?? null, {
										shouldValidate: true,
										shouldDirty: true
									});
								}}
								value={watch('brandIDs')}
								columns={brandNameAutocompleteColumns}
								selectionLabelAccessor={['Name']}
								name="brandIDs"
								multipleSelection
								firstItem={
									isAuthorized('/autocomplete/brand/addANewBrand')
										? {
												action: () =>
													NiceModal.show(BrandForm, {
														setBrand: (value) => {
															setValue('brandIDs', [value], {
																shouldValidate: true,
																shouldDirty: true
															});
														}
													}),
												label: <Typography color="var(--blue)">addANewBrand</Typography>,
												disabled: (dataSize) => isRestricted(SYSTEM_LIMITS.BRAND, dataSize),
												disabledTooltip: (
													<div style={{ display: 'block' }}>
														<ModuleToolTipContainer
															infoImage="lottie_multiBrand"
															title="multiBrandModule"
															description="multiBrandModuleSubtitle"
														/>

														<Button
															style={{ marginLeft: 'Auto', marginRight: 'Auto' }}
															className="mt-3"
															label="showPlans"
															icon={faEye}
															variant="blue"
															onClick={() => {
																track('moreThan3Brands');
																navigate(PATHS.OTO_MODULES);
															}}
														/>
													</div>
												)
											}
										: undefined
								}
							/>
						</FormItem>
					)}
					{(watch('userType') === 'warehouseUser' || watch('userType') === 'driverUser') && (
						<FormItem
							label="assignedWarehouses"
							isLoading={isLoading}
							roleName="/autocomplete/warehouse"
							required={watch('userType') === 'warehouseUser'}>
							<AutoComplete
								disabled={viewMode}
								autoCompleteName={AUTOCOMPLETE.WAREHOUSES}
								value={watch('warehouseIDs')}
								columns={brandNameAutocompleteColumns}
								selectionLabelAccessor={['Name']}
								name="warehouseIDs"
								multipleSelection
								setValue={(value) => {
									setValue('warehouseIDs', value?.map((item) => item.ID) ?? null, {
										shouldValidate: true,
										shouldDirty: true
									});
								}}
								firstItem={
									isAuthorized('/autocomplete/warehouse/addANewWarehouse')
										? {
												action: () =>
													NiceModal.show(WarehouseFormShort, {
														setWarehouse: (value) => {
															setValue(
																`warehouseIDs`,
																[...(watch('warehouseIDs') ?? null), value],
																{
																	shouldValidate: true,
																	shouldDirty: true
																}
															);
														}
													}),
												label: <Typography color="var(--blue)">addANewWarehouse</Typography>
											}
										: undefined
								}
							/>
						</FormItem>
					)}
					{(watch('userType') === 'branchUser' || watch('userType') === 'driverUser') && (
						<FormItem label="assignedBranches" isLoading={isLoading} roleName="/autocomplete/branch">
							<AutoComplete
								disabled={viewMode}
								autoCompleteName={AUTOCOMPLETE.BRANCHES}
								setValue={(value) => {
									setValue('branchIDs', value?.map((item) => item.ID) ?? null, {
										shouldValidate: true,
										shouldDirty: true
									});
								}}
								value={watch('branchIDs')}
								columns={brandNameAutocompleteColumns}
								selectionLabelAccessor={['Name']}
								name="branchIDs"
								multipleSelection
								firstItem={
									isAuthorized('/autocomplete/branch/addANewBranch')
										? {
												action: () =>
													NiceModal.show(BranchFormshort, {
														setBranch: (value) => {
															setValue(
																`branchIDs`,
																[...(watch('branchIDs') ?? []), value],
																{
																	shouldValidate: true,
																	shouldDirty: true
																}
															);
														}
													}),
												label: <Typography color="var(--blue)">addANewBranch</Typography>
											}
										: undefined
								}
							/>
						</FormItem>
					)}
				</Form>
			</ModalContent>
			{!viewMode && (
				<ModalActions>
					<Button label={'cancel'} variant="outline" onClick={handleCloseModal} />

					<Button
						loading={isLoading}
						label={mode === 'edit' ? 'save' : 'button.add'}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
					/>
				</ModalActions>
			)}
		</Modal>
	);
});

export default UserForm;
